import { notFound, OperationOutcomeError } from "@medplum/core";
import type { OperationOutcomeIssue } from "@medplum/fhirtypes";
import { getErrorStatusCode } from "@/lib/errors/get-error-status-code";
import { isNotFoundError } from "@/lib/errors/is-not-found-error";

const GONE_STATUS_CODES = new Set([410]);

/** Strict FHIR classification for callers that must not infer absence from error text. */
export function isMedplumResourceMissingOutcome(error: unknown): error is OperationOutcomeError {
  return (
    error instanceof OperationOutcomeError &&
    // Medplum synthesizes this singleton for non-JSON HTTP 404 responses, which do not confirm FHIR resource absence.
    error.outcome !== notFound &&
    !!error.outcome.issue.length &&
    error.outcome.issue.every((issue) => isMissingIssueCode(issue.code))
  );
}

function isMissingIssueCode(code: OperationOutcomeIssue["code"] | undefined): boolean {
  return code === "not-found" || code === "deleted";
}

function hasDeletedMessageToken(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const rawMessage = (error as { message?: unknown }).message;
  const message = typeof rawMessage === "string" ? rawMessage.toLowerCase() : "";

  return message.includes("deleted") || message.includes("gone");
}

/**
 * Detects Medplum/FHIR error shapes that indicate a missing or deleted resource.
 */
export function isMedplumResourceMissingError(error: unknown): boolean {
  if (error instanceof OperationOutcomeError) {
    const hasMissingIssue =
      error.outcome?.issue?.some((issue) => isMissingIssueCode(issue.code)) ?? false;

    if (hasMissingIssue) {
      return true;
    }
  }

  if (isNotFoundError(error)) {
    return true;
  }

  const statusCode = getErrorStatusCode(error);
  if (statusCode !== undefined) {
    return GONE_STATUS_CODES.has(statusCode);
  }

  return hasDeletedMessageToken(error);
}

/**
 * Resolves to `undefined` when a Medplum read fails because the resource is missing or
 * soft-deleted; rethrows every other failure. For by-id reads where a deleted record is an
 * expected state, not an error.
 */
export async function undefinedIfMissing<T>(read: Promise<T>): Promise<T | undefined> {
  try {
    return await read;
  } catch (error) {
    if (isMedplumResourceMissingError(error)) return undefined;
    throw error;
  }
}
